// Radio (Brief 4 / P4a) — the eyes-free face, as a hub. Lists the live sessions
// and lets you LISTEN to any one: a metered spoken summary of its trace, in the
// calm voice, with a spend meter + remaining + an off switch. Opt-in and yours
// to meter — replays are free; the SERVER prices and caps every clip. Each row's
// player fetches that session's trace on Listen (the hub doesn't load them
// upfront). Token-pure; cost in text.cost, never the reserved attention hue.

import { useEffect, useState } from "react";
import { getRadioPolicy } from "../api/client";
import type { RadioPolicy } from "@waytide/core";
import { usePresenceContext } from "@waytide/ui";
import { RadioControl } from "./RadioControl";
import { sessionLabel } from "@waytide/core";
import "./radio.css";

function usd(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(2)}`;
}

export function RadioPage() {
  const { sessions, loaded, error } = usePresenceContext();
  const [policy, setPolicy] = useState<RadioPolicy | null>(null);

  useEffect(() => {
    let alive = true;
    getRadioPolicy()
      .then((p) => alive && setPolicy(p))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="wt-radio">
      <header className="wt-radio__header">
        <h1 className="wt-radio__title">Radio</h1>
        <p className="wt-radio__subtitle">
          The eyes-free face — listen to any session in the calm voice. Opt-in
          and yours to meter: replaying a clip is free; switching one on draws on
          that session's budget while it runs, with a quiet meter and an off
          switch.
          {policy ? (
            <>
              {" "}
              Default cap{" "}
              <span className="wt-radio__cost" data-testid="radio-cap">
                {usd(policy.default_session_cap_micros)}
              </span>{" "}
              per session.
            </>
          ) : null}
        </p>
      </header>

      {error ? (
        <p className="wt-radio__empty">Couldn't reach your sessions: {error}</p>
      ) : !loaded ? (
        <p className="wt-radio__empty">Reading your sessions…</p>
      ) : sessions.length === 0 ? (
        <p className="wt-radio__empty">
          No active sessions to listen to yet. When an agent is working, it
          appears here.
        </p>
      ) : (
        <ul className="wt-radio__list" data-testid="radio-list">
          {sessions.map((s) => (
            <li key={s.thread_id} className="wt-radio__row">
              <span
                className="wt-radio__session"
                title={`Session #${s.thread_id}`}
              >
                {sessionLabel(s)}
              </span>
              <RadioControl threadId={s.thread_id} label={sessionLabel(s)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
